<template>
  <div>
    <!-- Multiple Managers - Clickable Context Card -->
    <!-- Show if user has multiple managers available to switch -->
    <div 
      v-if="canSwitchManagers" 
      class="manager-context-card mx-3 my-3"
      @click="showSwitchDialog = true"
    >
      <v-card class="context-card" elevation="0">
        <div class="context-content" :class="`bg-gradient-${getRoleColor(currentRole)}`">
          <div class="context-text">
            <div class="context-label">AKTIVE ORGANISATION</div>
            <div class="context-name">{{ currentForManagerName || 'Organisation wählen' }}</div>
            <div class="context-role">({{ getRoleDisplayName(currentRole) }})</div>
          </div>
          <i class="material-icons-round switch-icon">sync_alt</i>
        </div>
      </v-card>
    </div>

    <!-- Single Manager - Info Display (not clickable) -->
    <div v-else-if="currentForManagerName" class="manager-context-card mx-3 my-3">
      <v-card class="context-card context-card-static" elevation="0">
        <div class="context-content" :class="`bg-gradient-${getRoleColor(currentRole)}`">
          <div class="context-text">
            <div class="context-label">DEINE ORGANISATION</div>
            <div class="context-name">{{ currentForManagerName }}</div>
            <div class="context-role">({{ getRoleDisplayName(currentRole) }})</div>
          </div>
        </div>
      </v-card>
    </div>

    <!-- Context Switch Dialog -->
    <v-dialog v-model="showSwitchDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="pa-5">
          <h5 class="text-h5 font-weight-bold text-typo mb-0">
            Organisation wechseln
          </h5>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-0">
          <v-list class="py-2">
            <v-list-item
              v-for="(manager, index) in accessibleManagers"
              :key="`manager-${manager.id}-${manager.role}-${index}`"
              @click="selectManager(manager.id)"
              class="manager-item px-5 py-3"
              :class="{ 'active-item': manager.id === currentForManagerId }"
            >
              <v-list-item-content>
                <v-list-item-title class="text-body-1 font-weight-bold text-typo mb-1">
                  {{ manager.name }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-sm">
                  <span class="text-muted">{{ getRoleDisplayName(manager.role) }}</span>
                  <span v-if="manager.isOwn" class="ml-2 text-primary font-weight-medium">(Eigene)</span>
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action v-if="manager.id === currentForManagerId">
                <i class="material-icons-round text-success">check_circle</i>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="showSwitchDialog = false">
            Abbrechen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';

export default {
  name: 'ManagerSelector',
  data() {
    return {
      showSwitchDialog: false
    };
  },
  computed: {
    ...mapGetters([
      'hasMultipleManagers',
      'accessibleManagers',
      'currentForManagerId',
      'currentForManagerName',
      'currentRole'
    ]),
    
    // Check if user can actually switch between multiple managers
    canSwitchManagers() {
      // Only show switch icon if there are multiple managers available
      return this.accessibleManagers && this.accessibleManagers.length > 1;
    }
  },
  methods: {
    ...mapActions(['setCurrentForManager']),
    ...mapMutations(['isSwitchingContext']),
    
    getRoleDisplayName(role) {
      const roleNames = {
        'admin': 'Administrator',
        'manager': 'Manager',
        'pos': 'POS-Nutzer',
        'checker': 'Kontrolleur'
      };
      return roleNames[role] || role;
    },
    
    getRoleIcon(role) {
      const roleIcons = {
        'admin': 'shield',
        'manager': 'manage_accounts',
        'pos': 'point_of_sale',
        'checker': 'qr_code_scanner'
      };
      return roleIcons[role] || 'account_circle';
    },
    
    getRoleColor(role) {
      const roleColors = {
        'admin': 'danger',
        'manager': 'primary',
        'pos': 'success',
        'checker': 'info'
      };
      return roleColors[role] || 'secondary';
    },
    
    async selectManager(managerId) {
      if (managerId !== this.currentForManagerId) {
        this.isSwitchingContext(true);
        this.showSwitchDialog = false;
        this.setCurrentForManager(managerId);
        
        // Reload /me endpoint immediately to get updated context data
        await this.$store.dispatch('userInformations');
        
        // Wait for overlay animation to complete (~2 seconds)
        await new Promise(resolve => setTimeout(resolve, 2100));
        
        // Reload events and other data without hard reload
        if (this.$store.dispatch('getEventsAdmin')) {
          await this.$store.dispatch('getEventsAdmin');
        }
        
        // Hide overlay after data is loaded
        this.isSwitchingContext(false);
        
        // Determine redirect route based on user's permissions in new context
        const isAdmin = this.$store.getters.isAdmin;
        const isManager = this.$store.getters.isManager;
        const isPosUser = this.$store.getters.isPosUser;
        const isChecker = this.$store.getters.isChecker;
        const isAdminInOwnContext = isAdmin && !this.$store.getters.adminSimulated && 
                                     managerId === this.$store.getters.ownManagerId;
        
        let redirectRoute = null;
        
        if (isAdminInOwnContext || isManager) {
          // Admin im eigenen Kontext oder Manager: Dashboard
          redirectRoute = '/admin/analytics';
        } else if (isPosUser) {
          // POS-Nutzer: POS-Ticket erstellen
          redirectRoute = '/admin/pos/create-ticket';
        } else if (isChecker) {
          // Checker: Scanner Info-Seite (nur Info, kein Zugriff auf Funktionen)
          redirectRoute = '/admin/tickets/scan';
        } else {
          // Fallback: Dashboard
          redirectRoute = '/admin/analytics';
        }
        
        // Redirect to appropriate route
        if (redirectRoute && this.$route.path !== redirectRoute) {
          console.log('🔄 [MANAGER SELECTOR] Redirecting to:', redirectRoute, {
            isAdminInOwnContext,
            isManager,
            isPosUser,
            isChecker
          });
          this.$router.push(redirectRoute).catch(() => {});
        }
        
        // Emit event to refresh current page data
        this.$root.$emit('context-switched');
      } else {
        // Same manager selected, just close dialog
        this.showSwitchDialog = false;
      }
    }
  }
};
</script>

<style scoped>
/* Context Card */
.context-card {
  border-radius: 0.75rem;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

/* Clickable card (when multiple managers available) */
.manager-context-card:first-child .context-card:not(.context-card-static) {
  cursor: pointer;
}

.manager-context-card:first-child .context-card:not(.context-card-static):hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

/* Static card (when only one manager available) - no pointer, no hover */
.context-card-static {
  cursor: default !important;
}

.context-card-static:hover {
  transform: none !important;
  box-shadow: none !important;
}

.context-content {
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.context-text {
  flex: 1;
}

.context-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 4px;
}

.context-name {
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  line-height: 1.3;
}

.context-role {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 2px;
}

.switch-icon {
  color: rgba(255, 255, 255, 0.9);
  font-size: 20px;
}

/* Dialog List Items */
.manager-item {
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-left: 3px solid transparent;
}

.manager-item:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.manager-item.active-item {
  background-color: rgba(76, 175, 80, 0.08);
  border-left-color: #4caf50;
}
</style>
